import React, { useEffect, useState } from "react";
import {
  DndContext,
  DragOverlay,
  closestCorners,
  PointerSensor,
  useSensor,
  useSensors,
  rectIntersection,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  horizontalListSortingStrategy,
} from "@dnd-kit/sortable";
import {
  Container,
  AddColumnModal,
  AddTaskModal,
  DeleteColumnModal,
  TaskDetail,
  DeleteTaskModal,
  UpdateTaskModal,
} from "./";
import { Item } from "./sortable-item";
import { Card, Input, Tooltip } from "antd";
import { KanbanSkeleton } from "../../../../../components/page-skeleton";
import { useSelector } from "react-redux";
import { Add, Search } from "@mui/icons-material";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import TaskHistory from "./tssk-history";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";

export function SortableContainer(props) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({
      id: props.id,
      data: {
        type: "container",
        status: props.status,
      },
    });

  const style = {
    flex: 1,
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      {props.children}
    </div>
  );
}

export default function Board() {
  // STATES
  const [isLoading, setLoading] = useState(true);
  const [addPreview, setaddPreview] = useState(false);
  const [deletePreview, setDeletePreview] = useState(false);
  const [statuses, setStatuses] = useState([]);
  const [selectedStatus, setselectedStatus] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeId, setActiveId] = useState();
  const [tasks, setTasks] = useState([]);
  const [items, setItems] = useState({});
  const [selectedTask, setSelectedTask] = useState(null);
  const [addTaskPreview, setAddTaskPreview] = useState(false);
  const [taskDetailPreview, setTaskDetailPreview] = useState(false);
  const [editTaskPreview, setEditTaskPreview] = useState(false);
  const [deleteTaskPreview, setdeleteTaskPreview] = useState(false);
  const [taskHistoryPreview, setTaskHistoryPreview] = useState(false);
  const [containerActiveId, setContainerActiveId] = useState(null);

  // REDUX DATA
  const selectedCompany = useSelector((state) => state.CompanyReducer.selected_company);

  // CONST VALS
  const history = useHistory();
  const searchedTasks = history.location.search;
  const { http } = global.services;
  const filteredTasks = tasks?.filter(
    (task) =>
      task?.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      task?.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    })
  );

  // HELPERS
  const getTasksHelper = (statuses) => {
    http
      .get(`company-task/${selectedCompany.id}`)
      .then(({ data }) => {
        const items = {};
        statuses.forEach((item) => {
          const filtered = data.filter((x) => x.status.id === item.id);
          filtered.sort((a, b) => a.priority - b.priority);
          items[`status-${item.id}`] = filtered.map((x) => x.id);
        });
        setItems(items);
        setTasks(data);
        setStatuses(statuses);
        setLoading(false);
        if (searchedTasks) {
          const urlParams = new URLSearchParams(searchedTasks);
          const taskId = urlParams.get("task");
          if (taskId) {
            const task = data.find((t) => t.tag === taskId);
            if (task) return taskDetailHandler(task);
            toast.error("Task not found");
          }
        }
      })
      .catch(() => {
        setLoading(false);
      });
  };

  const getColumnsHelper = () => {
    setLoading(true);
    http
      .get(`company-board/${selectedCompany.id}`)
      .then(({ data }) => {
        if (data.length > 0) {
          getTasksHelper(data);
          return;
        }
        setStatuses(data);
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  };

  // HANDLERS
  const addPreviewHandler = () => setaddPreview(!addPreview);
  const addSuccessHandler = (status) => {
    setItems((prev) => ({
      ...prev,
      [`status-${status.id}`]: [],
    }));
    setStatuses([...statuses, status]);
  };

  const deletePreviewHandler = (status) => {
    setselectedStatus(status);
    setDeletePreview(!deletePreview);
  };
  const deleteSuccessHandler = (status) => {
    setStatuses(statuses.filter((s) => s.id !== status));
  };

  const addTaskPreviewHandler = (status) => {
    setselectedStatus(status);
    setAddTaskPreview(true);
  };
  const addTaskSuccessHandler = (task) => {
    setTasks([...tasks, task]);
    setItems((prev) => {
      const key = `status-${task.status.id}`;
      return {
        ...prev,
        [key]: [...prev[key], task.id],
      };
    });
  };

  const taskDetailHandler = (task) => {
    setSelectedTask(task);
    setTaskDetailPreview(true);
  };

  const editTaskPreviewHandler = () => {
    setTaskDetailPreview(!taskDetailPreview);
    setEditTaskPreview(!editTaskPreview);
  };
  const editTaskSuccessHandler = (task) => {
    setTasks(tasks.map((t) => (t.id === task.id ? task : t)));
    setSelectedTask(task);
  };

  const deleteTaskPreviewHandler = () => {
    setTaskDetailPreview(!taskDetailPreview);
    setdeleteTaskPreview(!deleteTaskPreview);
  };
  const deleteTaskSuccessHandler = (task) => {
    setdeleteTaskPreview(false);
    setTasks(tasks.filter((t) => t.id !== task.id));
    setItems((prev) => {
      const key = `status-${task.status.id}`;
      return {
        ...prev,
        [key]: prev[key].filter((taskId) => taskId !== task.id),
      };
    });
  };

  const taskHistoryPreviewHandler = () => {
    setTaskDetailPreview(!taskDetailPreview);
    setTaskHistoryPreview(!taskHistoryPreview);
  };

  // New Container Drag Handlers
  function handleContainerDragStart(event) {
    const { active } = event;
    setContainerActiveId(active.id);
  }

  function handleContainerDragEnd(event) {
    const { active, over } = event;

    if (!over) return;

    if (active.id !== over.id) {
      setStatuses((prevStatuses) => {
        const oldIndex = prevStatuses.findIndex(
          (status) => `status-${status.id}` === active.id
        );
        const newIndex = prevStatuses.findIndex(
          (status) => `status-${status.id}` === over.id
        );
        const reorderedStatuses = arrayMove(prevStatuses, oldIndex, newIndex);
        http
          .patch(`company-board`, {
            new_order: reorderedStatuses.map((status, index) => ({
              id: status.id,
              order: index + 1,
            })),
          })
          .catch(() => {});

        return reorderedStatuses;
      });
    }

    setContainerActiveId(null);
  }

  // Existing Drag Handlers
  function findContainer(id) {
    if (id in items) {
      return id;
    }

    return Object.keys(items).find((key) => items[key].includes(id));
  }

  function handleDragStart(event) {
    const { active } = event;
    const { id } = active;

    setActiveId(id);
  }

  function handleDragOver(event) {
    const { active, over } = event;
    if (!over) return;

    const activeId = active.id;
    const overId = over.id;

    const activeContainer = findContainer(activeId);
    const overContainer = findContainer(overId) || overId;

    if (!activeContainer || !overContainer || activeContainer === overContainer) {
      return;
    }

    setItems((prev) => {
      const activeItems = [...prev[activeContainer]];
      const overItems = [...(prev[overContainer] || [])];

      const activeIndex = activeItems.indexOf(activeId);

      if (activeIndex === -1) return prev;

      activeItems.splice(activeIndex, 1);
      overItems.push(activeId);

      return {
        ...prev,
        [activeContainer]: activeItems,
        [overContainer]: overItems,
      };
    });
  }

  function handleDragEnd(event) {
    const { active, over } = event;
    const { id } = active;
    const { id: overId } = over;

    const activeContainer = findContainer(id);
    const overContainer = findContainer(overId);

    if (!activeContainer || !overContainer || activeContainer !== overContainer) {
      return;
    }

    const activeIndex = items[activeContainer].indexOf(active.id);
    const overIndex = items[overContainer].indexOf(overId);

    if (activeIndex !== overIndex) {
      setItems((items) => {
        const updatedItem = {
          ...items,
          [overContainer]: arrayMove(items[overContainer], activeIndex, overIndex),
        };
        const newOrder = updatedItem[overContainer].map((taskId, index) => ({
          id: taskId,
          status: parseInt(overContainer.split("-")[1]),
          priority: index + 1,
        }));

        http.patch(`company-task/${id}`, { new_order: newOrder }).catch(() => {});
        return updatedItem;
      });
    } else {
      const newOrder = items[overContainer].map((taskId, index) => ({
        id: taskId,
        status: parseInt(overContainer.split("-")[1]),
        priority: index + 1,
      }));
      http.patch(`company-task/${id}`, { new_order: newOrder }).catch(() => {});
    }

    setActiveId(null);
  }

  // HOOKS
  useEffect(() => {
    if (!selectedCompany?.id) return;
    getColumnsHelper();
  }, [selectedCompany, searchedTasks]);

  return (
    <Card className="tasks-wrapper">
      {isLoading ? (
        <KanbanSkeleton />
      ) : (
        <>
          <div className="search-box">
            <Input
              prefix={<Search />}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search"
            />
          </div>
          <div className="board">
            <div style={{ display: "flex", gap: 20 }}>
              <DndContext
                sensors={sensors}
                collisionDetection={rectIntersection}
                onDragStart={(event) => {
                  if (event.active.data.current?.type === "container") {
                    handleContainerDragStart(event);
                  } else {
                    handleDragStart(event);
                  }
                }}
                onDragOver={handleDragOver}
                onDragEnd={(event) => {
                  if (event.active.data.current?.type === "container") {
                    handleContainerDragEnd(event);
                  } else {
                    handleDragEnd(event);
                  }
                }}
              >
                <SortableContext
                  items={statuses.map((status) => `status-${status.id}`)}
                  strategy={horizontalListSortingStrategy}
                >
                  {statuses.map((status, index) => (
                    <SortableContainer
                      key={index}
                      id={`status-${status.id}`}
                      status={status}
                    >
                      <Container
                        key={index}
                        id={`status-${status.id}`}
                        items={items[`status-${status.id}`].filter((taskId) =>
                          filteredTasks.some((task) => task.id === taskId)
                        )}
                        status={status}
                        tasks={tasks}
                        statuses={statuses}
                        setStatuses={setStatuses}
                        deletePreviewHandler={() => deletePreviewHandler(status)}
                        addTaskPreviewHandler={() => addTaskPreviewHandler(status)}
                        taskDetailHandler={taskDetailHandler}
                        dragType="container"
                      />
                    </SortableContainer>
                  ))}
                </SortableContext>

                <div
                  className={`add-status-card ${
                    statuses.length === 0 ? "stretched" : ""
                  }`}
                  style={{ minHeight: 500 }}
                >
                  <Tooltip title="Add task status column">
                    <button onClick={addPreviewHandler}>
                      <Add />
                    </button>
                  </Tooltip>
                  {statuses.length === 0 && <p>Add task status column</p>}
                </div>

                <DragOverlay>
                  {activeId ? (
                    <Item
                      fromDraggable
                      id={activeId}
                      data={tasks.find((x) => x.id === activeId)}
                    />
                  ) : containerActiveId ? (
                    <Container
                      id={containerActiveId}
                      items={items[containerActiveId].filter((taskId) =>
                        filteredTasks.some((task) => task.id === taskId)
                      )}
                      status={statuses.find(
                        (status) =>
                          status.id === parseInt(containerActiveId.split("-")[1])
                      )}
                      tasks={tasks}
                      statuses={statuses}
                      setStatuses={setStatuses}
                      deletePreviewHandler={() => {}}
                      addTaskPreviewHandler={() => {}}
                      taskDetailHandler={() => {}}
                      dragType="container"
                    />
                  ) : null}
                </DragOverlay>
              </DndContext>
            </div>
          </div>
        </>
      )}

      <AddColumnModal
        preview={addPreview}
        previewHandler={addPreviewHandler}
        successHandler={addSuccessHandler}
      />
      <DeleteColumnModal
        preview={deletePreview}
        previewHandler={() => setDeletePreview(false)}
        selectedColumn={selectedStatus}
        successHandler={deleteSuccessHandler}
        preventDelete={tasks.some((task) => task.status.id === selectedStatus?.id)}
      />
      <AddTaskModal
        status={selectedStatus?.id}
        preview={addTaskPreview}
        previewHandler={() => setAddTaskPreview(false)}
        successHandler={addTaskSuccessHandler}
      />
      <TaskDetail
        data={selectedTask}
        preview={taskDetailPreview}
        previewHandler={() => setTaskDetailPreview(false)}
        deleteHandler={deleteTaskPreviewHandler}
        editHandler={editTaskPreviewHandler}
        historyHandler={taskHistoryPreviewHandler}
      />
      <TaskHistory
        data={selectedTask}
        preview={taskHistoryPreview}
        previewHandler={taskHistoryPreviewHandler}
      />
      <DeleteTaskModal
        data={selectedTask}
        successHandler={deleteTaskSuccessHandler}
        preview={deleteTaskPreview}
        previewHandler={deleteTaskPreviewHandler}
      />
      <UpdateTaskModal
        data={selectedTask}
        preview={editTaskPreview}
        previewHandler={editTaskPreviewHandler}
        successHandler={editTaskSuccessHandler}
      />
    </Card>
  );
}
